import React, { useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Button } from "./Controls/Button";
import { PlanPriceAddReq, PlanPriceDetails } from "./Data/ApiTypes";
import { CurrencyDollar } from "react-bootstrap-icons";
import { cents, toCents } from "./Data/Money";
import { MoneyInput } from "./Controls/MoneyInput";

interface PlanPriceAddModalProps {
    show:    boolean;
    setShow: (show: boolean) => void;
    onSave:  (data: PlanPriceAddReq) => void;
    prices:  PlanPriceDetails[];
}

interface FormData {
    price: number;
}

export function generateCouponCode(existingCouponCodes: string[] | null): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code;
    do {
        code = Array.from({ length: 5 }, () =>
            chars[Math.floor(Math.random() * chars.length)]
        ).join('');
    } while (existingCouponCodes && existingCouponCodes.includes(code));
    return code;
}

export function PlanPriceAddModal({ show, setShow, onSave, prices }: PlanPriceAddModalProps) {
    const { register, handleSubmit, formState: { errors }, reset, clearErrors, setError } = useForm<FormData>({
        defaultValues: {
            price: 0.00
        }
    });

    const handleClose = () => {
        reset({ price: 0.00 });
        clearErrors("price");
        setShow(false);
    };

    const onSubmit = (data: FormData) => {
        if (data.price <= 0) {
            setError("price", {
                type: "manual",
                message: "Price must be greater than 0"
            });
            return;
        }

        // Check for duplicate price
        const priceExists = prices.some(p => toCents(p.price) === toCents(cents(data.price)));
        if (priceExists) {
            setError("price", {
                type: "manual",
                message: "This price already exists in the plan"
            });
            return;
        }

        onSave({
            name: `$${data.price.toFixed(2)}`,
            price: cents(data.price),
            couponCode: generateCouponCode(prices.map(x => x.couponCode))
        });
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Plan Price</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <MoneyInput
                        icon={<CurrencyDollar />}
                        label="Price"
                        explanation="The monthly price for this plan"
                        inputClassName="text-end"
                        {...register("price", {
                            required: "Price is required",
                            valueAsNumber: true
                        })}
                        error={errors.price}
                    />
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" className="btn-main">
                            Add
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}